import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, Trash2, Save, XCircle, ScanBarcode,
    Wallet, Smartphone, CreditCard, ArrowLeft, CheckCircle,
    Banknote, Landmark, Coins, HandCoins, Receipt,
    Building2, QrCode, Ticket, CircleEllipsis, ArrowRightLeft,
    PiggyBank, Briefcase, Loader2, Printer, Utensils, Plus, Bed
} from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import API, { socket } from '../../services/api';
import Sidebar from '../../components/Sidebar';
import { PricingService } from '../../services/pricing.service';
import RestoPrint from './restoprint';
import { ConversionStockService } from '../../utils/converisonstock';

const RestaurantPos = () => {
    const navigate = useNavigate();
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const USER_ID = currentUser.id || 'USR-1';
    const COMPANY_ID = currentUser.company_id || currentUser.companyId || 'CPY-1';
    const genererIdLot = () => `LOT-RESTO-${Date.now().toString().slice(-6)}`;

    // --- ÉTATS ---
    const [articlesMenu, setArticlesMenu] = useState([]);        
    const [articlesLibres, setArticlesLibres] = useState([]);   
    const [fichesTechniques, setFichesTechniques] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [modeSaisie, setModeSaisie] = useState('MENU');
    const [selectedMenuFT, setSelectedMenuFT] = useState(null); 
    const [selectedArt, setSelectedArt] = useState(null);       
    const [libelleGlobalMenu, setLibelleGlobalMenu] = useState('');
    const [qteSaisie, setQteSaisie] = useState('1'); 
    const [saisieGros, setSaisieGros] = useState(''); 
    const [saisieDetail, setSaisieDetail] = useState(''); 
    const [panierMenus, setPanierMenus] = useState([]);        
    const [panierLibres, setPanierLibres] = useState([]);      
    const [currentLotId, setCurrentLotId] = useState(genererIdLot());
    const [showValidation, setShowValidation] = useState(false);
    const [montantRecu, setMontantRecu] = useState('');
    const [monnaieARendre, setMonnaieARendre] = useState(0);
    const [moyenPaiement, setMoyenPaiement] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [alertMsg, setAlertMsg] = useState({ text: '', type: '' });
    const [paymentMapping, setPaymentMapping] = useState({});
    const [isLocked, setIsLocked] = useState(false);
    const [formatImpression, setFormatImpression] = useState('A6');
    const [isHebergementActive, setIsHebergementActive] = useState(false);
    const [chambresActives, setChambresActives] = useState([]);
    const [selectedLigneChambreId, setSelectedLigneChambreId] = useState('');
    const [chambreSearchQuery, setChambreSearchQuery] = useState('');
    const [isChambreDropdownOpen, setIsChambreDropdownOpen] = useState(false);
    const [isRoomPostActive, setIsRoomPostActive] = useState(false);

    const printRef = useRef();
    const [printData, setPrintData] = useState(null);
    const [dynamiqueCompanyPrint, setDynamiqueCompanyPrint] = useState({
        name: currentUser.company_name || currentUser.companyName || "LEDI EXPERT PRO",
        address: currentUser.company_address || currentUser.companyAddress || "Adresse non renseignée",
        phone: currentUser.company_phone || currentUser.companyPhone || "Tél: N/A",
        email: currentUser.company_email || currentUser.companyEmail || "Email: N/A",
        logo_data: currentUser.company_logo || currentUser.logo_data || currentUser.logo || null
    });

    const showToast = useCallback((text, type = 'success') => {
        setAlertMsg({ text, type });
        setTimeout(() => setAlertMsg({ text: '', type: '' }), 3000);
    }, []);

    const getArticleNom = (art) => {
        if (!art) return '';
        return art.nom || art.libelle || art.designation || art.name || 'ARTICLE SANS NOM';
    };

    const formaterStockPOS = useCallback((art) => {
        if (!art) return "-";
        const valeurStock = art.stock_actuel !== undefined ? art.stock_actuel : (art.stock || 0);
        if (typeof valeurStock === 'string' && valeurStock.includes('+')) {
            return valeurStock.replace(/-/g, '');
        }
        const qtePieces = Math.abs(Number(valeurStock)) || 0;
        return ConversionStockService.toExpressionTextuelle(qtePieces, art);
    }, []);

    const totalGeneral = useMemo(() => {
        const menusSafe = Array.isArray(panierMenus) ? panierMenus : [];
        const libresSafe = Array.isArray(panierLibres) ? panierLibres : [];
        
        const totalM = menusSafe.reduce((sum, item) => sum + Number(item?.montant_ttc_ligne || 0), 0);
        const totalL = libresSafe.reduce((sum, item) => sum + Number(item?.montant_ttc_ligne || 0), 0);
        return totalM + totalL;
    }, [panierMenus, panierLibres]);

    const loadSavedCartFromServer = useCallback(async () => {
        try {
            const res = await API.get(`/sales/temporary-cart/${USER_ID}`);
            if (res.data && res.data.lignes) {
                const parsedData = typeof res.data.lignes === 'string' ? JSON.parse(res.data.lignes) : res.data.lignes;
                
                if (parsedData && typeof parsedData === 'object' && !Array.isArray(parsedData)) {
                    setPanierMenus(Array.isArray(parsedData.panierMenus) ? parsedData.panierMenus : []);
                    setPanierLibres(Array.isArray(parsedData.panierLibres) ? parsedData.panierLibres : []);
                    if (parsedData.libelleGlobalMenu) setLibelleGlobalMenu(parsedData.libelleGlobalMenu);
                    if (parsedData.currentLotId) setCurrentLotId(parsedData.currentLotId);
                } else if (Array.isArray(parsedData)) {
                    setPanierMenus([]);
                    setPanierLibres(parsedData);
                } else {
                    setPanierMenus([]);
                    setPanierLibres([]);
                }
            }
        } catch (err) {
            try {
                const savedMenus = localStorage.getItem(`restaurant_panier_menus_${USER_ID}`);
                const savedLibres = localStorage.getItem(`restaurant_panier_libres_${USER_ID}`);
                const savedLibelle = localStorage.getItem(`restaurant_libelle_${USER_ID}`);
                const savedLot = localStorage.getItem(`restaurant_lot_${USER_ID}`);
                
                setPanierMenus(savedMenus ? JSON.parse(savedMenus) : []);
                setPanierLibres(savedLibres ? JSON.parse(savedLibres) : []);
                if (savedLibelle) setLibelleGlobalMenu(savedLibelle);
                if (savedLot) setCurrentLotId(savedLot);
            } catch (e) {
                setPanierMenus([]);
                setPanierLibres([]);
            }
        }
    }, [USER_ID]);

    useEffect(() => {
        const saveCartToBackendAndLocal = async () => {
            const payloadData = {
                panierMenus: Array.isArray(panierMenus) ? panierMenus : [],
                panierLibres: Array.isArray(panierLibres) ? panierLibres : [],
                libelleGlobalMenu,
                currentLotId
            };
            
            localStorage.setItem(`restaurant_panier_menus_${USER_ID}`, JSON.stringify(payloadData.panierMenus));
            localStorage.setItem(`restaurant_panier_libres_${USER_ID}`, JSON.stringify(payloadData.panierLibres));
            localStorage.setItem(`restaurant_libelle_${USER_ID}`, libelleGlobalMenu);
            localStorage.setItem(`restaurant_lot_${USER_ID}`, currentLotId);
            try {
                await API.post('/sales/temporary-cart', {
                    vendeur_id: USER_ID,
                    company_id: COMPANY_ID,
                    lignes: JSON.stringify(payloadData)
                });
                socket?.emit('cart_changed', { userId: USER_ID, companyId: COMPANY_ID });
            } catch (e) {
                console.error("Erreur synchronisation panier serveur:", e);
            }
        };
        const timer = setTimeout(saveCartToBackendAndLocal, 600);
        return () => clearTimeout(timer);
    }, [panierMenus, panierLibres, libelleGlobalMenu, currentLotId, USER_ID, COMPANY_ID]);

    useEffect(() => {
        const fetchCompanySettings = async () => {
            try {
                const res = await API.get('/company/settings');
                if (res.data) {
                    const data = res.data.success && res.data.data ? res.data.data : res.data;
                    setDynamiqueCompanyPrint({
                        name: data.name || data.nom || data.raison_sociale || "LEDI EXPERT PRO",
                        address: data.address || data.adresse || "Adresse non renseignée",
                        phone: data.phone || data.telephone || "Tél: N/A",
                        email: data.email || "Email: N/A",
                        logo_data: data.logo_data || data.logo || data.logo_url || null
                    });
                    const activeModules = data.modules_actifs || data.allowed_modules || [];
                    const moduleHebergementActif = Boolean(
                        data.module_hebergement === 1 || 
                        data.module_hebergement === '1' ||
                        data.moduleHebergement === true || 
                        data.has_hebergement === true ||
                        data.hebergement === true ||
                        (Array.isArray(activeModules) && activeModules.some(m => String(m).toLowerCase().includes('hebergement'))) ||
                        (() => {
                            try {
                                const lic = JSON.parse(localStorage.getItem('licenseStatus'));
                                return lic?.allowed_modules && lic.allowed_modules.some(m => String(m).toLowerCase().includes('hebergement'));
                            } catch(e) {
                                return false;
                            }
                        })()
                    );
                    setIsHebergementActive(moduleHebergementActif);
                }
            } catch (err) {
                console.error("Erreur paramètres entreprise:", err);
            }
        };
        fetchCompanySettings();
        loadSavedCartFromServer();
    }, [loadSavedCartFromServer]);

    useEffect(() => {
        if (!isHebergementActive) return;
        const fetchChambresActives = async () => {
            try {
                const res = await API.get('/hebergement/reservations/lignes-chambres');
                const chambresData = res.data?.success && res.data?.data 
                    ? res.data.data 
                    : (Array.isArray(res.data) ? res.data : []);
                setChambresActives(chambresData);
            } catch (err) {
                setChambresActives([]);
            }
        };
        fetchChambresActives();
    }, [isHebergementActive]);

    const fetchArticlesAndMenus = useCallback(async () => {
        try {
            const [artRes, ftRes] = await Promise.all([
                API.get('/products'),
                API.get('/technical-sheets').catch(() => ({ data: [] }))
            ]);
            
            const allProducts = Array.isArray(artRes.data) ? artRes.data : [];
            const fiches = Array.isArray(ftRes.data) ? ftRes.data : (ftRes.data?.data || []);
            setFichesTechniques(fiches);
            const ftProductIds = new Set(
                fiches
                    .filter(ft => ft.is_active === 1 && (ft.product_id || ft.semi_fini_id))
                    .map(ft => String(ft.product_id || ft.semi_fini_id))
            );
            const menusFiltres = allProducts.filter(prod => {
                const estMenu = Number(prod.est_menu || prod.estMenu || prod.is_menu || 0) === 1;
                const aUneFicheTechnique = ftProductIds.has(String(prod.id));
                return estMenu && aUneFicheTechnique;
            });
            setArticlesMenu(menusFiltres);
            const libresFiltres = allProducts.filter(prod => {
                const estMenu = Number(prod.est_menu || prod.estMenu || prod.is_menu || 0) === 1;
                return !estMenu;
            });
            setArticlesLibres(libresFiltres);
        } catch (err) { 
            console.error("Erreur chargement articles/menus:", err); 
        }
    }, []);

    const fetchPaymentMethods = useCallback(async () => {
        try {
            const res = await API.get('/plan-comptable/paiements/methodes');
            if (res.data && res.data.success) {
                const actives = res.data.data.filter(m => m.is_active === 1 && m.is_pos === 1);
                setPaymentMethods(actives);
                const mapping = {};
                res.data.data.forEach(m => { mapping[m.code.toUpperCase()] = m.libelle; });
                setPaymentMapping(mapping);
                if (actives.length > 0 && !moyenPaiement) {
                    setMoyenPaiement(actives[0].code);
                }
            }
        } catch (err) { console.error("Erreur paiement:", err); }
    }, [moyenPaiement]);

    useEffect(() => {
        fetchArticlesAndMenus();
        fetchPaymentMethods();
    }, [fetchArticlesAndMenus, fetchPaymentMethods]);

    useEffect(() => {
        const recu = parseFloat(montantRecu) || 0;
        const calculMonnaie = Number((recu - totalGeneral).toFixed(2));
        setMonnaieARendre(calculMonnaie < 0 ? 0 : calculMonnaie);
    }, [montantRecu, totalGeneral]);

    const handleAjouterMenuAuPanier = async () => {
        if (!selectedMenuFT) {
            showToast("Veuillez sélectionner un menu / plat dans la liste.", "error");
            return;
        }
        const libelleGlobalNet = (libelleGlobalMenu || '').trim();
        if (!libelleGlobalNet) {
            showToast("⚠️ Erreur : Veuillez d'abord saisir le libellé global du menu en haut.", "error");
            return;
        }
        const qteLots = Math.abs(parseFloat(qteSaisie) || 1);
        try {
            const ftAssociee = fichesTechniques.find(ft => String(ft.product_id || ft.semi_fini_id) === String(selectedMenuFT.id));
            if (!ftAssociee) {
                showToast("Aucune fiche technique active n'est rattachée à cet article.", "error");
                return;
            }
            const ftDetailRes = await API.get(`/technical-sheets/${ftAssociee.id}`);
            const lignesComposants = Array.isArray(ftDetailRes.data?.items) ? ftDetailRes.data.items : [];
            const prixUnitaireMenu = Number(selectedMenuFT.prixVente || selectedMenuFT.prix_vente || 1500);
            const montantTotalMenu = prixUnitaireMenu * qteLots;
            const taxeActiveFront = Number(selectedMenuFT.taxeactive ?? selectedMenuFT.taxeActive ?? 0) === 1 ? 1 : 0;
            const tauxTaxeFront = taxeActiveFront === 1 ? Number(selectedMenuFT.taxetaux ?? selectedMenuFT.taxeTaux ?? 0) : 0;
            let htCalculeFront = Number((montantTotalMenu / (1 + (tauxTaxeFront / 100))).toFixed(2));
            let taxeCalculeeFront = Number((montantTotalMenu - htCalculeFront).toFixed(2));
            if (taxeActiveFront === 0) {
                htCalculeFront = montantTotalMenu;
                taxeCalculeeFront = 0;
            }
            const idMenuUnique = `MENU-${Date.now()}`;
            const ligneMenuPrincipale = {
                id: idMenuUnique,
                product_id: selectedMenuFT.id,
                ft_id: ftAssociee.id,
                quantite_lots: qteLots,
                nom_article_snap: `${libelleGlobalNet.toUpperCase()} - ${getArticleNom(selectedMenuFT).toUpperCase()}`,
                type_ligne: 'VENTE',
                is_menu_parent: true,
                quantite: qteLots,
                prix_vente_unitaire: prixUnitaireMenu,
                remise_montant: 0,
                taxe_active: taxeActiveFront,
                taxe_taux_snap: tauxTaxeFront,
                taxe_montant: taxeCalculeeFront,
                montant_ht: htCalculeFront,
                montant_ht_ligne: htCalculeFront,
                montant_ttc_ligne: montantTotalMenu,
                observation: `Menu restaurant - ${qteLots} portion(s)`,
                composants: []
            };
            const sousLignes = lignesComposants.map((comp, idx) => {
                const compNom = comp.designation || comp.matiere_nom || comp.psf_nom || comp.nom || comp.libelle || 'Composant';
                const qteReqUnitaire = Number(comp.quantite_requise || 0);
                const qteTotaleComp = qteReqUnitaire * (qteLots / (Number(ftAssociee.quantite_base) || 1));
                
                return {
                    id: `${idMenuUnique}-sub-${idx}`,
                    product_id: comp.matiere_id || comp.composant_semi_fini_id || comp.id,
                    nom_article_snap: `    ↳ ${compNom}`,
                    type_ligne: 'VENTE_DETAIL_MENU',
                    is_menu_child: true,
                    parent_id: idMenuUnique,
                    quantite: qteTotaleComp,
                    prix_vente_unitaire: 0,
                    remise_montant: 0,
                    taxe_active: 0,
                    taxe_taux_snap: 0,
                    taxe_montant: 0,
                    montant_ht: 0,
                    montant_ht_ligne: 0,
                    montant_ttc_ligne: 0,
                    observation: `Composant de ${libelleGlobalNet}`
                };
            });
            setPanierMenus(prev => [...(Array.isArray(prev) ? prev : []), ligneMenuPrincipale, ...sousLignes]);
            setSelectedMenuFT(null);
            setQteSaisie('1');
            showToast(`✅ Élément ajouté sous le libellé "${libelleGlobalNet}".`);
        } catch (err) {
            showToast("Erreur lors de l'ajout des composants du menu.", "error");
        }
    };

    const handleAjouterArticleLibre = () => {
        if (!selectedArt) return;
        const gClean = String(saisieGros || '0').replace(/-/g, '').replace(',', '.').trim();
        const dClean = String(saisieDetail || '0').replace(/-/g, '').replace(',', '.').trim();
        const valGros = Math.abs(parseFloat(gClean) || 0);
        const valDetail = Math.abs(parseFloat(dClean) || 0);
        if (valGros === 0 && valDetail === 0) {
            showToast("❌ Veuillez saisir une quantité (Gros ou Détail).", "error");
            return;
        }
        const coeffLogistique = Math.abs(Number(selectedArt.unit_coefficient || selectedArt.coefficient || selectedArt.unit_coeff || 1)) || 1;
        const stockBrutBDD = selectedArt.stock_actuel !== undefined ? selectedArt.stock_actuel : (selectedArt.stock || 0);
        let stockTotalDisponiblePieces = 0;
        if (typeof stockBrutBDD === 'string' && stockBrutBDD.includes('+')) {
            const parties = stockBrutBDD.split('+');
            const grosEntiers = Math.abs(parseFloat(parties[0]) || 0);
            const detailRestants = Math.abs(parseFloat(parties[1]) || 0);
            stockTotalDisponiblePieces = Number(((grosEntiers * coeffLogistique) + detailRestants).toFixed(4));
        } else {
            const valeurBruteNettoyee = typeof stockBrutBDD === 'string' ? parseFloat(stockBrutBDD.replace(/-/g, '')) : Number(stockBrutBDD);
            stockTotalDisponiblePieces = Math.abs(Number((valeurBruteNettoyee || 0).toFixed(4)));
        }
        const piecesDemandeesAujourdhui = Number(((valGros * coeffLogistique) + valDetail).toFixed(4));
        const panierLibresSafe = Array.isArray(panierLibres) ? panierLibres : [];
        const indexExistant = panierLibresSafe.findIndex(item => item.product_id === selectedArt.id);
        let piecesDejaDansLePanier = 0;
        if (indexExistant !== -1) {
            const itemExistant = panierLibresSafe[indexExistant];
            const gExistant = Math.abs(parseFloat(itemExistant.saisie_gros || 0));
            const dExistant = Math.abs(parseFloat(itemExistant.saisie_detail || 0));
            piecesDejaDansLePanier = Number(((gExistant * coeffLogistique) + dExistant).toFixed(4));
        }
        const piecesDemandeesTotales = Number((piecesDejaDansLePanier + piecesDemandeesAujourdhui).toFixed(4));
        if (piecesDemandeesTotales > stockTotalDisponiblePieces) {
            const expressionDispoTxt = formaterStockPOS({ ...selectedArt, stock_actuel: stockTotalDisponiblePieces });
            showToast(`❌ Stock insuffisant ! Disponible en rayon : ${expressionDispoTxt}`, 'error');
            return;
        }
        const qteEquivalentGrosPourTarification = piecesDemandeesTotales / coeffLogistique;
        const calculs = PricingService.calculerLigne(selectedArt, qteEquivalentGrosPourTarification);
        const stockAvantGros = Number((stockTotalDisponiblePieces / coeffLogistique).toFixed(4));
        const stockApresGros = Number((Math.max(0, stockTotalDisponiblePieces - piecesDemandeesTotales) / coeffLogistique).toFixed(4));
        
        const gFinal = indexExistant !== -1 ? Number((Math.abs(parseFloat(panierLibresSafe[indexExistant].saisie_gros || 0)) + valGros).toFixed(4)) : valGros;
        const dFinal = indexExistant !== -1 ? Number((Math.abs(parseFloat(panierLibresSafe[indexExistant].saisie_detail || 0)) + valDetail).toFixed(4)) : valDetail;
        const meta = ConversionStockService.getMetadata(selectedArt);
        const codeGrosNet = meta.codeGros || selectedArt.code || 'CS';
        const refDetailNet = meta.refDetail || selectedArt.unite_reference || 'BTS';
        const prixBrutUnitaireReference = Math.abs(Number(selectedArt.prixVente || selectedArt.prix_vente || 0));
        const taxeActiveFront = Number(selectedArt.taxeactive ?? selectedArt.taxeActive ?? 0) === 1 ? 1 : 0;
        const tauxTaxeFront = taxeActiveFront === 1 ? Number(selectedArt.taxetaux ?? selectedArt.taxeTaux ?? 0) : 0;
        const mtTTCFront = Number(Number(calculs.netAPayer || 0).toFixed(2));
        let htCalculeFront = Number(Number(calculs.montantHT || 0).toFixed(2));
        let taxeCalculeeFront = Number(Number(calculs.montantTaxe || 0).toFixed(2));
        if (htCalculeFront <= 0 && mtTTCFront > 0) {
            const tFront = tauxTaxeFront / 100;
            if (taxeActiveFront === 1 && tauxTaxeFront > 0) {
                htCalculeFront = Number((mtTTCFront / (1 + tFront)).toFixed(2));
                taxeCalculeeFront = Number((mtTTCFront - htCalculeFront).toFixed(2));
            } else {
                htCalculeFront = mtTTCFront;
                taxeCalculeeFront = 0;
            }
        }
        const itemData = {
            id: indexExistant !== -1 ? panierLibresSafe[indexExistant].id : `ART-LIBRE-${Date.now()}`,
            product_id: selectedArt.id,
            nom_article_snap: getArticleNom(selectedArt).toUpperCase(),
            unite_libelle_snap: codeGrosNet,
            unite_snap: refDetailNet,        
            unite_gros: codeGrosNet,
            unite_detail: refDetailNet,
            ratio_conversion: coeffLogistique,
            prix_vente_unitaire: Number(Number(prixBrutUnitaireReference).toFixed(2)),
            quantite: qteEquivalentGrosPourTarification,
            saisie_gros: gFinal,
            saisie_detail: dFinal,
            qte_achetee: `${gFinal}+${dFinal}`,
            qte_vendue_formatee: `${piecesDemandeesTotales} ${refDetailNet}`,
            remise_montant: Number(Number(calculs.remiseTotale || 0).toFixed(2)),
            type_remise_snap: calculs.typeRemise,
            taxe_active: taxeActiveFront,
            taxe_taux_snap: Number(tauxTaxeFront.toFixed(2)),
            taxe_montant: taxeCalculeeFront,
            montant_ht: htCalculeFront,
            montant_ht_ligne: htCalculeFront,
            montant_ttc_ligne: mtTTCFront,
            stock_avant_vente: stockAvantGros,
            stock_apres_vente: stockApresGros,
            id_lot: currentLotId,
            user_id: USER_ID,
            company_id: COMPANY_ID,
            statut: 'actif',
            type_ligne: 'VENTE',
            is_menu_parent: false,
            is_menu_child: false,
            observation: 'Article libre / Boisson'
        };
        let nouveauPanierLibres = [...panierLibresSafe];
        if (indexExistant !== -1) {
            nouveauPanierLibres[indexExistant] = itemData;
        } else {
            nouveauPanierLibres = [itemData, ...nouveauPanierLibres];
        }
        setPanierLibres(nouveauPanierLibres);
        setSelectedArt(null);
        setSaisieGros('');
        setSaisieDetail('');
        setSearchTerm('');
        showToast(`✅ ${getArticleNom(selectedArt)} ajouté.`);
    };

    const viderPaniersComplet = async () => {
        setPanierMenus([]);
        setPanierLibres([]);
        setLibelleGlobalMenu('');
        localStorage.removeItem(`restaurant_panier_menus_${USER_ID}`);
        localStorage.removeItem(`restaurant_panier_libres_${USER_ID}`);
        localStorage.removeItem(`restaurant_libelle_${USER_ID}`);
        try {
            await API.delete(`/sales/temporary-cart/${USER_ID}`);
        } catch (e) {
            console.error("Erreur nettoyage panier serveur:", e);
        }
    };

    const finaliserVente = async (options = {}) => {
        const menusSafe = Array.isArray(panierMenus) ? panierMenus : [];
        const libresSafe = Array.isArray(panierLibres) ? panierLibres : [];
        const menusParentsPrincipaux = menusSafe.filter(item => !item.is_menu_child);
        const tousLesArticles = [...menusParentsPrincipaux, ...libresSafe];
        
        if (tousLesArticles.length === 0 || isSaving || isLocked) return false;
        
        const isChambre = options.isChambre || false;
        const totalSecurise = Math.abs(totalGeneral);
        if (!isChambre) {
            const recu = Math.abs(parseFloat(montantRecu) || 0);
            if (recu < totalSecurise) {
                showToast(`❌ Encaissement insuffisant ! Manque : ${(totalSecurise - recu).toLocaleString()} F`, "error");
                return false;
            }
        }
        setIsSaving(true);
        try {
            const lignesValides = tousLesArticles.map(item => ({
                product_id: item.product_id,
                nom_article_snap: item.nom_article_snap,
                type_ligne: item.type_ligne || 'VENTE',
                quantite: Math.abs(item.quantite || 0),
                saisie_gros: Math.abs(item.saisie_gros || 0),
                saisie_detail: Math.abs(item.saisie_detail || 0),
                prix_vente_unitaire: Math.abs(item.prix_vente_unitaire || 0),
                remise_montant: Math.abs(item.remise_montant || 0),
                taxe_active: Number(item.taxe_active || 0),
                taxe_taux_snap: Number(item.taxe_taux_snap || 0),
                montant_ht: Math.abs(item.montant_ht_ligne || item.montant_ht || item.montant_ttc_ligne || 0),
                taxe_montant: Math.abs(item.taxe_montant || 0),
                montant_ttc_ligne: Math.abs(item.montant_ttc_ligne || 0),
                ft_id: item.ft_id || null,
                quantite_lots: item.quantite_lots || 0,
                observation: item.observation
            }));

            const observationTexte = libelleGlobalMenu.trim() 
                ? `Vente Restaurant - ${libelleGlobalMenu.trim()}` 
                : `Vente Restaurant (Articles libres) - ${currentLotId}`;

            const dataVente = {
                lignes: lignesValides,
                staff_id: USER_ID,
                staff_name: currentUser.username || "utilisateur",
                caissier_id: USER_ID,
                observation: observationTexte,
                encaissement: {
                    lot_id: currentLotId,
                    total: totalSecurise,
                    recu: isChambre ? 0 : Math.abs(parseFloat(montantRecu) || 0),
                    rendu: isChambre ? 0 : Math.max(0, monnaieARendre),
                    moyen_paiement: isChambre ? 'CREDIT_CHAMBRE' : moyenPaiement,
                    nom_client: options.nomClient || "CLIENT AU COMPTANT"
                },
                ligne_chambre_id: options.ligne_chambre_id || null
            };

            const response = await API.post('/restaurant-ventes/sale', dataVente);
            
            if (response.data.success) {
                const messageSucces = isChambre ? `✅ Addition postée avec succès sur la chambre !` : `✅ VENTE RESTAURANT ${currentLotId} ENREGISTRÉE !`;
                showToast(messageSucces);

                const nouveauPrintData = {
                    panier: [...menusSafe, ...libresSafe],
                    venteInfo: {
                        reference: currentLotId,
                        lot_id: currentLotId,
                        est_definitive: true,
                        client_nom: options.nomClient || "CLIENT AU COMPTANT",
                        table_name_snap: libelleGlobalMenu.trim() || null,
                        vendeur: currentUser.username || "utilisateur",
                        mode_paiement: moyenPaiement,
                        montant_recu: isChambre ? 0 : Math.abs(parseFloat(montantRecu) || 0),
                        reliquat: isChambre ? 0 : Math.max(0, monnaieARendre)
                    },
                    company: dynamiqueCompanyPrint
                };
                setPrintData(nouveauPrintData);

                await viderPaniersComplet();
                setMontantRecu('');
                setShowValidation(false);
                setIsRoomPostActive(false);
                setSelectedLigneChambreId('');
                setChambreSearchQuery('');
                
                const nouveauLot = genererIdLot();
                setCurrentLotId(nouveauLot);
                localStorage.setItem(`restaurant_lot_${USER_ID}`, nouveauLot);
                if (socket) {
                    socket.emit('cart_changed', { userId: USER_ID, companyId: COMPANY_ID });
                    socket.emit('STOCK_UPDATED');
                }
                await fetchArticlesAndMenus();

                setTimeout(() => {
                    handlePrint();
                }, 250);

                return true;
            }
            return false;
        } catch (err) {
            showToast(err.response?.data?.error || "Erreur lors de l'enregistrement de la vente", "error");
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handlePosterEnChambreSubmit = async () => {
        let finalLigneChambreId = selectedLigneChambreId;
        const chambresActivesSafe = Array.isArray(chambresActives) ? chambresActives : [];
        if (!finalLigneChambreId && chambreSearchQuery) {
            const match = chambresActivesSafe.find(ch => {
                const num = String(ch.chambre_numero || ch.numero || '').toLowerCase();
                const cli = String(ch.client_principal_nom || ch.client_nom || '').toLowerCase();
                const query = chambreSearchQuery.toLowerCase();
                return num.includes(query) || cli.includes(query);
            });
            if (match) {
                finalLigneChambreId = match.reservation_ligne_id || match.id;
            }
        }
        if (!finalLigneChambreId) {
            showToast("Veuillez sélectionner une chambre valide dans la liste déroulante.", "error");
            return;
        }
        const chambreObj = chambresActivesSafe.find(c => String(c.reservation_ligne_id || c.id) === String(finalLigneChambreId));
        const nomClientChambre = chambreObj ? (chambreObj.client_principal_nom || chambreObj.client_nom || 'CLIENT CHAMBRE') : 'CLIENT CHAMBRE';
        await finaliserVente({
            isChambre: true,
            ligne_chambre_id: finalLigneChambreId,
            nomClient: nomClientChambre
        });
    };

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `FACTURE_RESTAURANT_${currentLotId}`
    });

    const finaliserVenteAvecImpression = async (formatChoisi = 'A6') => {
        setFormatImpression(formatChoisi);
        await finaliserVente({ isChambre: false });
    };

    const panierMenusSafe = Array.isArray(panierMenus) ? panierMenus : [];
    const panierLibresSafe = Array.isArray(panierLibres) ? panierLibres : [];

    return (
        <div style={layoutStyle}>
            {alertMsg.text && (
                <div style={{ ...toastStyle, backgroundColor: alertMsg.type === 'error' ? '#EF4444' : '#1E40AF' }}>
                    <CheckCircle size={18} /> {alertMsg.text}
                </div>
            )}
            <Sidebar />
            <main style={mainStyle}>
                {showValidation && <div style={blockerOverlay} />}
                <div style={colGauche}>
                    <div style={cardHeader}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                                type="button"
                                style={modeSaisie === 'MENU' ? btnTabActive : btnTabInactive}
                                onClick={() => { setModeSaisie('MENU'); setSearchTerm(''); setSelectedArt(null); }}
                            >
                                <Utensils size={16} /> COMPOSER UN MENU
                            </button>
                            <button 
                                type="button"
                                style={modeSaisie === 'ARTICLE' ? btnTabActive : btnTabInactive}
                                onClick={() => { setModeSaisie('ARTICLE'); setSearchTerm(''); setSelectedMenuFT(null); }}
                            >
                                <Plus size={16} /> ARTICLES LIBRES / BOISSONS
                            </button>
                        </div>
                    </div>
                    <div style={searchSection}>
                        {modeSaisie === 'MENU' && (
                            <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', padding: '12px', borderRadius: '8px', marginBottom: '14px' }}>
                                <label style={{ ...labelStyle, color: '#92400E', fontSize: '11px', marginBottom: '6px' }}>
                                    ⚠️ LIBELLÉ GLOBAL DU MENU (OBLIGATOIRE POUR LES MENUS)
                                </label>
                                <input 
                                    type="text"
                                    style={minimalInputLibelleGlobal}
                                    placeholder="Ex: Pâte avec sauce graine, Menu spécial..."
                                    value={libelleGlobalMenu}
                                    onChange={(e) => setLibelleGlobalMenu(e.target.value)}
                                />
                            </div>
                        )}
                        <div style={{ marginBottom: '12px' }}>
                            <div style={inputWithIcon}>
                                <Search size={16} color="#94A3B8" />
                                <input 
                                    style={minimalInput} 
                                    placeholder={modeSaisie === 'MENU' ? "Rechercher un plat ou composant..." : "Rechercher une boisson / article..."}
                                    value={searchTerm} 
                                    onChange={(e) => setSearchTerm(e.target.value)} 
                                />
                            </div>
                        </div>
                        <div style={tableWrapper}>
                            <table style={smallTable}>
                                <thead>
                                    <tr>
                                        <th style={thSmall}>DÉSIGNATION</th>
                                        <th style={thSmall}>STOCK ACTUEL</th>
                                        <th style={thSmall}>PRIX</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modeSaisie === 'MENU' ? (
                                        articlesMenu
                                            .filter(art => getArticleNom(art).toLowerCase().includes(searchTerm.toLowerCase()))
                                            .map(art => {
                                                const nomArt = getArticleNom(art).toUpperCase();
                                                return (
                                                    <tr 
                                                        key={art.id} 
                                                        onClick={() => setSelectedMenuFT(art)} 
                                                        style={{ background: selectedMenuFT?.id === art.id ? '#FEF9C3' : 'transparent', cursor: 'pointer' }}
                                                    >
                                                        <td style={tdSmall}><strong>{nomArt}</strong></td>
                                                        <td style={tdSmall}>
                                                            <span style={{ background: '#F8FAFC', padding: '2px 6px', borderRadius: '4px', border: '1px solid #CBD5E1', fontSize: '11px', fontWeight: '700', display: 'inline-block' }}>
                                                                {formaterStockPOS(art)}
                                                            </span>
                                                        </td>
                                                        <td style={tdSmall}>{Number(art.prixVente || art.prix_vente || 1500).toLocaleString()} F</td>
                                                    </tr>
                                                );
                                            })
                                    ) : (
                                        articlesLibres
                                            .filter(art => {
                                                const stockVal = Number(art.stock_actuel !== undefined ? art.stock_actuel : (art.stock || 0));
                                                const matchesSearch = getArticleNom(art).toLowerCase().includes(searchTerm.toLowerCase());
                                                if (searchTerm.trim() !== '') return matchesSearch;
                                                return stockVal > 0;
                                            })
                                            .map(art => (
                                                <tr 
                                                    key={art.id} 
                                                    onClick={() => {
                                                        setSelectedArt(art);
                                                        setSaisieGros('');
                                                        setSaisieDetail('');
                                                    }} 
                                                    style={{ background: selectedArt?.id === art.id ? '#FEF9C3' : 'transparent', cursor: 'pointer' }}
                                                >
                                                    <td style={tdSmall}><strong>{getArticleNom(art).toUpperCase()}</strong></td>
                                                    <td style={tdSmall}>
                                                        <span style={{ background: '#F8FAFC', padding: '2px 6px', borderRadius: '4px', border: '1px solid #CBD5E1', fontSize: '11px', fontWeight: '700', display: 'inline-block' }}>
                                                            {formaterStockPOS(art)}
                                                        </span>
                                                    </td>
                                                    <td style={tdSmall}>{Number(art.prixVente || art.prix_vente || 0).toLocaleString()} F</td>
                                                </tr>
                                            ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div style={saisieBox}>
                            {modeSaisie === 'MENU' ? (
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={labelStyle}>SÉLECTION ACTUELLE (MENU)</label>
                                        <div style={inputDisabled}>{selectedMenuFT ? getArticleNom(selectedMenuFT) : 'Aucun menu sélectionné'}</div>
                                    </div>
                                    <div style={{ width: '90px' }}>
                                        <label style={labelStyle}>PORTIONS</label>
                                        <input 
                                            type="number" 
                                            min="1"
                                            style={minimalInputSaisie}
                                            value={qteSaisie}
                                            onChange={(e) => setQteSaisie(e.target.value)}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                        <button 
                                            type="button"
                                            style={{
                                                ...btnAjouter,
                                                opacity: (!selectedMenuFT || !libelleGlobalMenu.trim()) ? 0.5 : 1
                                            }}
                                            disabled={!selectedMenuFT || !libelleGlobalMenu.trim()}
                                            onClick={handleAjouterMenuAuPanier}
                                        >
                                            AJOUTER
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label style={labelStyle}>SÉLECTION ACTUELLE (ARTICLE / BOISSON LIBRE)</label>
                                    <div style={inputDisabled}>
                                        {selectedArt ? `${getArticleNom(selectedArt).toUpperCase()} (${Number(selectedArt.prixVente || selectedArt.prix_vente || 0).toLocaleString()} F)` : 'AUCUN ARTICLE SÉLECTIONNÉ'}
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                        {selectedArt && Number(selectedArt.unit_coefficient || selectedArt.coefficient || 1) > 1 && (
                                            <div style={{ flex: 1 }}>
                                                <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#64748b', display: 'block', marginBottom: '2px' }}>
                                                    EN GROS {`(${String(selectedArt.unit_code_gros || selectedArt.unite_code || selectedArt.code || 'CS').toUpperCase()})`}
                                                </span>
                                                <input
                                                    type="number"
                                                    step="any"
                                                    min="0"
                                                    style={{ ...minimalInputSaisie, fontWeight: '900', background: 'white' }}
                                                    value={saisieGros}
                                                    onChange={(e) => setSaisieGros(e.target.value.replace(/[^0-9.]/g, ''))}
                                                    placeholder="0"
                                                />
                                            </div>
                                        )}
                                        <div style={{ flex: 1 }}>
                                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#64748b', display: 'block', marginBottom: '2px' }}>
                                                AU DÉTAIL {selectedArt ? `(${String(selectedArt.unit_ref_detail || selectedArt.unite_reference || 'UNITÉ').toUpperCase()})` : ''}
                                            </span>
                                            <input
                                                type="number"
                                                step="any"
                                                min="0"
                                                style={{ ...minimalInputSaisie, fontWeight: '900', background: 'white' }}
                                                value={saisieDetail}
                                                onChange={(e) => setSaisieDetail(e.target.value.replace(/[^0-9.]/g, ''))}
                                                placeholder="0"
                                            />
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <button
                                                type="button"
                                                style={{
                                                    ...btnAjouter,
                                                    opacity: (!selectedArt || (saisieGros === '' && saisieDetail === '')) ? 0.5 : 1
                                                }}
                                                disabled={!selectedArt || (saisieGros === '' && saisieDetail === '')}
                                                onClick={handleAjouterArticleLibre}
                                            >
                                                AJOUTER
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div style={colDroite}>
                    <div style={{...saisieSection, flex: showValidation ? 1 : 0, background: showValidation ? '#EFF6FF' : 'transparent', padding: showValidation ? '20px' : '0', zIndex: 1001}}>
                        {!showValidation ? (
                            <div style={panierSection}>
                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', background:'#0F172A', padding:'10px 15px', flexWrap: 'wrap', gap: '8px'}}>
                                    <span style={{color:'#fff', fontWeight:'bold', fontSize:'12px'}}>LOT : {currentLotId}</span>
                                    <div style={{display:'flex', gap:'8px', flexWrap: 'wrap'}}>
                                        {/* Bouton Poster en chambre affiché dynamiquement si l'hébergement est actif */}
                                        {isHebergementActive && (
                                            <button 
                                                type="button" 
                                                style={{
                                                    ...btnEnregistrer, 
                                                    background: isRoomPostActive ? '#0369a1' : '#e0f2fe', 
                                                    color: isRoomPostActive ? '#fff' : '#0369a1',
                                                    border: '1px solid #bae6fd'
                                                }} 
                                                onClick={() => {
                                                    setIsRoomPostActive(!isRoomPostActive);
                                                    setSelectedLigneChambreId('');
                                                    setChambreSearchQuery('');
                                                }}
                                            >
                                                <Bed size={14}/> {isRoomPostActive ? 'FERMER CHAMBRE' : 'POSTER EN CHAMBRE'}
                                            </button>
                                        )}
                                        <button type="button" style={btnAnnuler} onClick={viderPaniersComplet}><Trash2 size={14}/> TOUT VIDER</button>
                                        <button type="button" disabled={(panierMenusSafe.length === 0 && panierLibresSafe.length === 0)} style={btnEnregistrer} onClick={() => { setShowValidation(true); setIsRoomPostActive(false); }}><Save size={14}/> VALIDER</button>
                                    </div>
                                </div>
                                {isHebergementActive && isRoomPostActive && (
                                    <div style={{ background: '#e0f2fe', padding: '15px', borderBottom: '3px solid #0369a1' }}>
                                        <div style={{ fontSize: '12px', fontWeight: '900', color: '#0369a1', marginBottom: '8px', textTransform: 'uppercase' }}>
                                            POSTER CETTE COMMANDE SUR UNE CHAMBRE (TOTAL : {Math.abs(totalGeneral).toLocaleString()} F)
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                            <div style={{ flex: 1, position: 'relative', minWidth: '220px' }}>
                                                <input
                                                    type="text"
                                                    placeholder="🔍 Rechercher N° chambre ou client..."
                                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #0369a1', fontSize: '13px', background: '#fff', outline: 'none' }}
                                                    value={chambreSearchQuery}
                                                    onChange={(e) => {
                                                        setChambreSearchQuery(e.target.value);
                                                        setIsChambreDropdownOpen(true);
                                                        if (!e.target.value) setSelectedLigneChambreId('');
                                                    }}
                                                    onFocus={() => setIsChambreDropdownOpen(true)}
                                                />
                                                {isChambreDropdownOpen && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        marginTop: '4px',
                                                        left: 0,
                                                        right: 0,
                                                        maxHeight: '130px',
                                                        overflowY: 'auto',
                                                        background: '#fff',
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: '8px',
                                                        zIndex: 1000,
                                                        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <div
                                                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}
                                                            onClick={() => {
                                                                setChambreSearchQuery('');
                                                                setSelectedLigneChambreId('');
                                                                setIsChambreDropdownOpen(false);
                                                            }}
                                                        >
                                                            -- Effacer la sélection --
                                                        </div>
                                                        {chambresActives
                                                            .filter(ch => {
                                                                const q = chambreSearchQuery.toLowerCase();
                                                                const num = String(ch.chambre_numero || ch.numero || '').toLowerCase();
                                                                const cli = String(ch.client_principal_nom || ch.client_nom || '').toLowerCase();
                                                                return num.includes(q) || cli.includes(q);
                                                            })
                                                            .map((ch, index) => {
                                                                const ligneChambreId = ch.reservation_ligne_id || ch.id;
                                                                const numeroChambre = ch.chambre_numero || ch.numero || '---';
                                                                const nomClient = ch.client_principal_nom || ch.client_nom || 'Client non assigné';
                                                                return (
                                                                    <div
                                                                        key={`${ligneChambreId}-${index}`}
                                                                        style={{
                                                                            padding: '8px 12px',
                                                                            cursor: 'pointer',
                                                                            fontSize: '13px',
                                                                            borderBottom: '1px solid #f1f5f9',
                                                                            background: String(selectedLigneChambreId) === String(ligneChambreId) ? '#f0fdf4' : '#fff'
                                                                        }}
                                                                        onClick={() => {
                                                                            setSelectedLigneChambreId(ligneChambreId);
                                                                            setChambreSearchQuery(`Chambre N° ${numeroChambre} — ${nomClient}`);
                                                                            setIsChambreDropdownOpen(false);
                                                                        }}
                                                                    >
                                                                        🏨 <strong style={{ color: '#0f172a' }}>Chambre N° {numeroChambre}</strong> — <span style={{ color: '#475569' }}>{nomClient}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                disabled={(!selectedLigneChambreId && !chambreSearchQuery) || isSaving || (panierMenusSafe.length === 0 && panierLibresSafe.length === 0)}
                                                onClick={handlePosterEnChambreSubmit}
                                                style={{
                                                    background: '#0369a1',
                                                    color: '#fff',
                                                    border: 'none',
                                                    padding: '10px 18px',
                                                    borderRadius: '8px',
                                                    fontWeight: 'bold',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    opacity: ((!selectedLigneChambreId && !chambreSearchQuery) || isSaving || (panierMenusSafe.length === 0 && panierLibresSafe.length === 0)) ? 0.5 : 1
                                                }}
                                            >
                                                {isSaving ? 'Traitement...' : 'VALIDER & POSTER'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* --- CONTENEUR DOUBLE PANIER 50/50 HARMONISÉ --- */}
                                <div style={containerDoublePanier5050}>
                                    <div style={panierSubSection50}>
                                        <div style={subPanierHeader}>
                                            <Utensils size={13} /> 1. MENUS & PLATS CUISINÉS ({panierMenusSafe.filter(i => !i.is_menu_child).length})
                                        </div>
                                        <div style={tableWrapperPanierScroll}>
                                            <table style={fullTable}>
                                                <thead>
                                                    <tr style={{ background: '#F8FAFC', position: 'sticky', top: 0, zIndex: 5 }}>
                                                        <th style={thMainCart}>DÉSIGNATION</th>
                                                        <th style={{ ...thMainCart, textAlign: 'center' }}>QTÉ</th>
                                                        <th style={thMainCart}>P.U</th>
                                                        <th style={thMainCart}>REMISE</th>
                                                        <th style={thMainCart}>TVA</th>
                                                        <th style={thMainCart}>TOTAL TTC</th>
                                                        <th style={thMainCart}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {panierMenusSafe.filter(i => !i.is_menu_child).length === 0 ? (
                                                        <tr><td colSpan="7" style={emptyRowStyle}>Aucun menu dans le panier</td></tr>
                                                    ) : (
                                                        panierMenusSafe
                                                            .filter(item => !item.is_menu_child)
                                                            .map((item, idx) => (
                                                                <tr key={item.id || idx} style={{ background: '#ffffff', borderBottom: '1px solid #F1F5F9' }}>
                                                                    <td style={{ ...tdMain, paddingLeft: '10px', color: '#1E293B', fontWeight: 'bold' }}>
                                                                        {item.nom_article_snap}
                                                                    </td>
                                                                    <td style={{ ...tdMain, textAlign: 'center', fontWeight: 'bold' }}>{item.quantite}</td>
                                                                    <td style={tdMain}>{Number(item.prix_vente_unitaire || 0).toLocaleString()} F</td>
                                                                    <td style={{ ...tdMain, color: '#EF4444' }}>{Number(item.remise_montant || 0) > 0 ? `-${Number(item.remise_montant).toLocaleString()} F` : '-'}</td>
                                                                    <td style={{ ...tdMain, color: '#64748B' }}>{Number(item.taxe_montant || 0) > 0 ? `${Number(item.taxe_montant).toLocaleString()} F` : '-'}</td>
                                                                    <td style={{ ...tdMain, fontWeight: '900', color: '#1E40AF' }}>{Number(item.montant_ttc_ligne || 0).toLocaleString()} F</td>
                                                                    <td style={tdMain}>
                                                                        <button type="button" style={btnDel} onClick={() => setPanierMenus(panierMenusSafe.filter(p => p.id !== item.id && p.parent_id !== item.id))}>
                                                                            <Trash2 size={13} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <div style={panierSubSection50}>
                                        <div style={{ ...subPanierHeader, background: '#0284C7' }}>
                                            <Plus size={13} /> 2. ARTICLES LIBRES & BOISSONS ({panierLibresSafe.length})
                                        </div>
                                        <div style={tableWrapperPanierScroll}>
                                            <table style={fullTable}>
                                                <thead>
                                                    <tr style={{ background: '#F8FAFC', position: 'sticky', top: 0, zIndex: 5 }}>
                                                        <th style={thMainCart}>DÉSIGNATION / QTÉ</th>
                                                        <th style={thMainCart}>P.U</th>
                                                        <th style={thMainCart}>REMISE</th>
                                                        <th style={thMainCart}>TVA</th>
                                                        <th style={thMainCart}>TOTAL TTC</th>
                                                        <th style={thMainCart}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {panierLibresSafe.length === 0 ? (
                                                        <tr><td colSpan="6" style={emptyRowStyle}>Aucun article libre ou boisson</td></tr>
                                                    ) : (
                                                        panierLibresSafe.map((item, idx) => (
                                                            <tr key={item.id || idx} style={{ background: '#ffffff', borderBottom: '1px solid #F1F5F9' }}>
                                                                <td style={{ ...tdMain, paddingLeft: '10px' }}>
                                                                    <div style={{ fontWeight: 'bold' }}>{item.nom_article_snap}</div>
                                                                    <div style={{ fontSize: '10px', color: '#64748B' }}>
                                                                        {(() => {
                                                                            const g = Math.abs(parseFloat(item.saisie_gros || 0));
                                                                            const d = Math.abs(parseFloat(item.saisie_detail || 0));
                                                                            const codeGros = item.unite_gros || 'CS';
                                                                            const refDetail = item.unite_detail || 'BTS';
                                                                            if (g > 0 && d > 0) return `${g} ${codeGros} + ${d} ${refDetail}`;
                                                                            if (g > 0) return `${g} ${codeGros}`;
                                                                            return `${d} ${refDetail}`;
                                                                        })()}
                                                                    </div>
                                                                </td>
                                                                <td style={tdMain}>{Number(item.prix_vente_unitaire || 0).toLocaleString()} F</td>
                                                                <td style={{ ...tdMain, color: '#EF4444' }}>{Number(item.remise_montant || 0) > 0 ? `-${Number(item.remise_montant).toLocaleString()} F` : '-'}</td>
                                                                <td style={{ ...tdMain, color: '#64748B' }}>{Number(item.taxe_montant || 0) > 0 ? `${Number(item.taxe_montant).toLocaleString()} F` : '-'}</td>
                                                                <td style={{ ...tdMain, fontWeight: '900', color: '#1E40AF' }}>{Number(item.montant_ttc_ligne || 0).toLocaleString()} F</td>
                                                                <td style={tdMain}>
                                                                    <button type="button" style={btnDel} onClick={() => setPanierLibres(panierLibresSafe.filter(p => p.id !== item.id))}>
                                                                        <Trash2 size={13} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <div style={totalContainer}>
                                    <div style={totalLabel}>TOTAL À ENCAISSER</div>
                                    <div style={totalValue}>{Math.abs(totalGeneral).toLocaleString()} F</div>
                                </div>
                            </div>
                        ) : (
                            <div style={encaissementContainer}>
                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                                    <label style={labelStyle}>ENCAISSEMENT</label>
                                    <button type="button" onClick={() => setShowValidation(false)} style={btnRetourAction}><ArrowLeft size={14}/> RETOUR</button>
                                </div>
                                <div style={paymentToggleRow}>
                                    {paymentMethods.map(method => (
                                        <button
                                            type="button"
                                            key={method.id}
                                            onClick={() => setMoyenPaiement(method.code)}
                                            style={moyenPaiement === method.code ? btnPayActive : btnPayInactive}
                                        >
                                            <span>{method.libelle}</span>
                                        </button>
                                    ))}
                                </div>
                                <div style={{marginTop:'10px'}}>
                                    <label style={labelStyle}>MONTANT REÇU</label>
                                    <input
                                        id="montant-recu-input"
                                        autoFocus
                                        style={inputRecuV2}
                                        type="number"
                                        placeholder="0"
                                        value={montantRecu}
                                        onChange={(e) => setMontantRecu(e.target.value)}
                                    />
                                </div>
                                <div style={monnaieStyleV2(monnaieARendre)}>MONNAIE : {monnaieARendre >= 0 ? Math.round(monnaieARendre).toLocaleString() : 0} F</div>
                                <div style={{display:'flex', gap:'10px', marginTop:'15px'}}>
                                    <button type="button" style={{...btnFinalV2, background: '#16a34a'}} onClick={() => finaliserVenteAvecImpression('A6')}>
                                        <Printer size={16}/> VALIDER & IMPRIMER (A6)
                                    </button>
                                    <button type="button" style={{...btnFinalV2, background: '#2563eb'}} onClick={() => finaliserVenteAvecImpression('A5')}>
                                        <Printer size={16}/> VALIDER & IMPRIMER (A5)
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
            {printData && (
                <div style={{ display: 'none' }}>
                    <RestoPrint
                        ref={printRef}
                        panier={printData.panier}
                        venteInfo={printData.venteInfo}
                        company={printData.company}
                        format={formatImpression}
                    />
                </div>
            )}
        </div>
    );
};

// =========================================================================
// STYLES CSS
// =========================================================================
const layoutStyle = { display: 'flex', height: '100vh', background: '#F8FAFC', fontFamily: 'system-ui, sans-serif' };
const mainStyle = { flex: 1, padding: '20px', display: 'flex', gap: '20px', overflow: 'hidden', position: 'relative' };
const colGauche = { flex: 1.1, display: 'flex', flexDirection: 'column' };
const colDroite = { flex: 1, display: 'flex', flexDirection: 'column' };
const cardHeader = { background: '#ffffff', padding: '12px', borderRadius: '12px', border: '1px solid #E2E8F0', marginBottom: '15px' };
const btnTabActive = { background: '#4F46E5', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' };
const btnTabInactive = { background: '#F1F5F9', color: '#64748B', border: 'none', padding: '10px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' };
const searchSection = { flex: 1, background: '#ffffff', borderRadius: '12px', border: '1px solid #E2E8F0', padding: '15px', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const saisieSection = { borderRadius: '12px', display: 'flex', flexDirection: 'column', height: '100%' };
const panierSection = { background: '#ffffff', borderRadius: '12px', border: '1px solid #CBD5E1', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' };
const containerDoublePanier5050 = { display: 'flex', flexDirection: 'column', flex: 1, height: 'calc(100% - 110px)', overflow: 'hidden' };
const panierSubSection50 = { flex: '1 1 50%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderBottom: '2px solid #E2E8F0' };
const tableWrapperPanierScroll = { overflowY: 'auto', flex: 1, background: '#fff' };
const toastStyle = { position: 'fixed', top: '25px', left: '50%', transform: 'translateX(-50%)', padding: '12px 25px', color: '#fff', borderRadius: '50px', zIndex: 9999, fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' };
const blockerOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255, 255, 255, 0.4)', zIndex: 1000 };
const encaissementContainer = { display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', padding: '15px', borderRadius: '12px' };
const paymentToggleRow = { display: 'flex', gap: '8px', marginTop: '8px' };
const btnPayActive = { flex: 1, background: '#4F46E5', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' };
const btnPayInactive = { flex: 1, background: '#fff', color: '#475569', border: '1px solid #CBD5E1', padding: '10px', borderRadius: '8px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' };
const inputRecuV2 = { width: '100%', padding: '10px', fontSize: '20px', fontWeight: '900', border: '2px solid #4F46E5', borderRadius: '8px', textAlign: 'center', outline: 'none' };
const monnaieStyleV2 = (m) => ({ padding: '10px', background: m >= 0 ? '#DCFCE7' : '#FEE2E2', color: m >= 0 ? '#15803D' : '#B91C1C', marginTop: '10px', borderRadius: '8px', fontWeight: '800', textAlign: 'center', fontSize: '14px' });
const btnFinalV2 = { flex: 1, color: '#fff', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' };
const labelStyle = { fontSize: '10px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block' };
const inputWithIcon = { display: 'flex', alignItems: 'center', gap: '10px', background: '#F8FAFC', padding: '10px 14px', borderRadius: '8px', border: '1px solid #CBD5E1' };
const minimalInput = { border: 'none', background: 'transparent', outline: 'none', fontSize: '14px', width: '100%' };
const minimalInputSaisie = { border: '1px solid #CBD5E1', background: '#fff', padding: '10px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', width: '100%', textAlign: 'center', outline: 'none' };
const minimalInputLibelleGlobal = { border: '1px solid #D97706', background: '#fff', padding: '10px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', width: '100%', textAlign: 'left', outline: 'none', color: '#1E293B' };
const inputDisabled = { background: '#E2E8F0', padding: '10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', color: '#334155' };
const saisieBox = { marginTop: '15px', background: '#F8FAFC', padding: '12px', borderRadius: '8px', border: '1px solid #E2E8F0' };
const tableWrapper = { overflowY: 'auto', flex: 1, borderRadius: '8px', border: '1px solid #F1F5F9' };
const smallTable = { width: '100%', borderCollapse: 'collapse' };
const thSmall = { textAlign: 'left', padding: '10px', background: '#0F172A', color: '#fff', fontSize: '11px', position: 'sticky', top: 0, zIndex: 10 };
const tdSmall = { padding: '10px', borderBottom: '1px solid #F1F5F9', fontSize: '13px' };
const btnEnregistrer = { background: '#10B981', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' };
const btnAnnuler = { background: '#fff', color: '#DC2626', border: '1px solid #FCA5A5', padding: '8px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' };
const btnRetourAction = { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: '#DC2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold', color: '#fff' };
const btnAjouter = { background: '#0F172A', color: '#fff', border: 'none', padding: '0 20px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', height: '40px' };
const subPanierHeader = { background: '#334155', color: '#fff', padding: '6px 10px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' };
const fullTable = { width: '100%', borderCollapse: 'collapse' };
const thMainCart = { background: '#F8FAFC', padding: '8px 10px', fontSize: '10px', fontWeight: '700', textAlign: 'left', color: '#475569', borderBottom: '1px solid #E2E8F0' };
const tdMain = { padding: '8px 10px', fontSize: '11px', color: '#1E293B', verticalAlign: 'middle' };
const emptyRowStyle = { padding: '15px', textAlign: 'center', color: '#94A3B8', fontSize: '12px', fontStyle: 'italic' };
const totalContainer = { display: 'flex', alignItems: 'center', borderTop: '2px solid #E2E8F0', background: '#F8FAFC' };
const totalLabel = { flex: 1, textAlign: 'right', padding: '12px 15px', fontWeight: '700', fontSize: '12px', color: '#475569' };
const totalValue = { padding: '12px 15px', fontWeight: '900', fontSize: '18px', background: '#4F46E5', color: '#ffffff', minWidth: '130px', textAlign: 'center' };
const btnDel = { color: '#EF4444', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };

export default RestaurantPos;